import { TFunction } from "i18next";
import {
  RosterProvider,
  RosterSource,
  RosterVendor,
} from "src/models/hierarchy";

/** What a district's roster comes from: a vendor, the last file import, or nothing yet (R9). */
export type RosterSourceKind = RosterVendor | "oneroster" | "simple" | "none";

export const isRosterVendor = (value: unknown): value is RosterVendor =>
  value === "clever" || value === "classlink";

/**
 * The district's roster source: the connected vendor when there is one, otherwise the format of the
 * last import (`lastImport`, or the legacy `provider: "csv"` with `format`), otherwise none (FR-035).
 */
export const rosterSourceKind = (
  source: RosterSource | null | undefined,
): RosterSourceKind => {
  if (!source) return "none";
  if (isRosterVendor(source.provider)) return source.provider;
  if (source.lastImport)
    return source.lastImport.format === "simple" ? "simple" : "oneroster";
  if (source.provider === "csv")
    return source.format === "simple-roster" ? "simple" : "oneroster";
  return "none";
};

/** The kind of source one sync row came from. */
export const syncSourceKind = (provider: RosterProvider): RosterSourceKind =>
  provider === "simple-roster"
    ? "simple"
    : provider === "csv"
      ? "oneroster"
      : provider;

/** Readable name: "Clever", "ClassLink", "CSV upload", "Roster sheet" or "Not set up". */
export const rosterSourceName = (t: TFunction, kind: RosterSourceKind) =>
  t(`rostering.sourceName.${kind}`);

/** When the source last brought in data: the vendor's last sync, or the last import. */
export const rosterSourceDate = (
  source: RosterSource | null | undefined,
): string | null => {
  const kind = rosterSourceKind(source);
  if (kind === "none") return null;
  if (isRosterVendor(kind)) return source?.lastSyncAt ?? null;
  return source?.lastImport?.at ?? source?.lastSyncAt ?? null;
};

/**
 * One line for the Districts list and the Overview: "Clever · last sync Oct 18, 2026",
 * "Roster sheet · imported Oct 18, 2026", or "Not set up".
 */
export const rosterSourceSummary = (
  t: TFunction,
  source: RosterSource | null | undefined,
  locale?: string,
) => {
  const kind = rosterSourceKind(source);
  const name = rosterSourceName(t, kind);
  const at = rosterSourceDate(source);
  if (!at) return name;
  const date = new Date(at).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return isRosterVendor(kind)
    ? t("rostering.sourceSynced", { source: name, date })
    : t("rostering.sourceImported", { source: name, date });
};

const DATE_TIME: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
};

/**
 * A date and time in the given IANA time zone, with the zone's abbreviation, e.g.
 * "Oct 20, 2026, 3:00 AM CDT". UTC when the zone is missing or unknown (FR-030).
 */
export const formatInTimeZone = (
  iso: string,
  timeZone: string | null | undefined,
  locale?: string,
) => {
  const date = new Date(iso);
  try {
    return new Intl.DateTimeFormat(locale, {
      ...DATE_TIME,
      timeZone: timeZone || "UTC",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, {
      ...DATE_TIME,
      timeZone: "UTC",
    }).format(date);
  }
};

/** A ClassLink OneRoster address the server accepts: `https://…/ims/oneroster/v1p1` (R4). */
export const isClassLinkBaseUrl = (value: string) =>
  /^https:\/\/[^\s/]+(\/[^\s]*)?\/ims\/oneroster\/v1p1\/?$/i.test(value.trim());
